import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import { useTenantStore } from '../../stores/useTenantStore';
import useErrorNavigation from './useErrorNavigation';
import { getTenantFromPath } from './useTenantNavigation';
import {
  authenticateUsingNhsAppIdentity,
  authenticateUsingNhsAppPatientIdentity,
  fetchNhsLoginUrlApi,
} from '../../services/nhs.service';
import { useUrlSegments } from './useUrlSegments';
import {
  NhsLoginAuthorizeResponse,
  PatientDetails,
  RequestTypeShort,
} from '../../types';
import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
import { fetchAuthenticatedPatientDetailsApi } from '../../services/session.service';

type NhsLoginStartOptions = {
  onNhsError?: (stx: { error: string; description?: string }) => void | Promise<void>;
};

export function useNhsLoginSso(deps: NhsLoginStartOptions = {}) {
  const route = useRoute();
  const { firstSegment } = useUrlSegments();
  const errorNavigation = useErrorNavigation();
  const tenantStore = useTenantStore();

  const nhsLoginError = computed(() => route.query?.error as string | undefined);
  const nhsLoginErrorDescription = computed(
    () => route.query?.error_description as string | undefined,
  );
  const nhsLoginCode = computed(() => route.query?.code as string | undefined);
  const assertedLoginIdentity = computed(() => route.query?.assertedLoginIdentity as string | undefined);

  async function checkForNhsErrorParams() {
    const err = nhsLoginError.value;
    if (err) {
      let error = 'sso-user-session-invalid';
      if(nhsLoginErrorDescription.value === 'ConsentNotGiven'){
        error = 'consent_required';
      }
      await deps.onNhsError?.({ error: error });
      await errorNavigation.navigateToErrorPage();
      return true;
    }
    return false;
  }

  onMounted(async () => checkForNhsErrorParams());

  async function verifySsoTokenWithNhsLogin(): Promise<void> {
    const requestTypeShort = firstSegment.value.replace('-request', '') as RequestTypeShort;
    const tenantId = getTenantFromPath(route.path);
    await tenantStore.fetchTenant(tenantId);
    window.location.href = await getNhsLoginRedirectUrl(
      requestTypeShort,
      assertedLoginIdentity.value,
    );
  }

  async function authenticateLoggedInUser(): Promise<boolean> {
    if(!nhsLoginCode.value){
      return authenticationError();
    }

    const requestType = firstSegment.value.replace('-request', '') as RequestTypeShort;
    const tenantId = getTenantFromPath(route.path);
    await tenantStore.fetchTenant(tenantId);

    const authenticationResult = await authenticateUsingNhsAppIdentity(nhsLoginCode.value, requestType);
    if (!authenticationResult.ok) {
      return authenticationError();
    }

    await tenantStore.fetchTenant(authenticationResult.data.tenantId);
    const loginResult = await authenticateUsingNhsAppPatientIdentity(authenticationResult.data.nhsLoginSSOToken);
    if(!loginResult.ok) {
      return authenticationError();
    }

    const store = getStoreForRequestType(requestType);
    const patientDetailsResult: { patientDetails: PatientDetails } | { ok: boolean } =
      await fetchAuthenticatedPatientDetailsApi();
    if ('patientDetails' in patientDetailsResult) {
      store.setNhsSsoDetails(patientDetailsResult.patientDetails);
    }

    return true;
  }

  function constructNhsLoginUrl(res: NhsLoginAuthorizeResponse): string {
    const { authUrl, client_id, redirect_uri, response_type, scope, state, nonce, vtr, assertedLoginIdentity } = res.loginRequest;
    const url = new URL(authUrl);
    url.searchParams.set('response_type', response_type);
    url.searchParams.set('scope', scope);
    url.searchParams.set('client_id', client_id);
    url.searchParams.set('redirect_uri', `${redirect_uri}`);
    url.searchParams.set('state', state);
    url.searchParams.set('nonce', nonce);
    url.searchParams.set('vtr', vtr);
    url.searchParams.set('prompt', 'none');
    url.searchParams.set('asserted_login_identity', assertedLoginIdentity);
    return url.toString();
  }

  async function getNhsLoginRedirectUrl(requestType: RequestTypeShort, assertedLoginIdentity: string): Promise<string> {
    const nhsLoginDetails = await fetchNhsLoginUrlApi(requestType, assertedLoginIdentity);
    if (!nhsLoginDetails.ok) {
      await authenticationError();
      throw new Error('Failed to get NHS login URL');
    }

    return constructNhsLoginUrl(nhsLoginDetails.data);
  }

  function getStoreForRequestType(requestType: RequestTypeShort) {
    if (requestType === 'medical') return useMedicalRequestStore();
    if (requestType === 'admin') return useAdminRequestStore();
    return null;
  }

  async function authenticationError(): Promise<boolean> {
    await deps.onNhsError?.({ error: 'sso-user-session-invalid' });
    await errorNavigation.navigateToErrorPage();
    return false;
  }

  return {
    assertedLoginIdentity,
    verifySsoTokenWithNhsLogin: verifySsoTokenWithNhsLogin,
    authenticateLoggedInUser,
    checkForNhsErrorParams,
  };
}
